import {
  cloneTypedArray,
  // fromHalfFloat,
  newConstructor,
  // toHalfFloat,
  type TypedArray
} from "@zyzgroup/core-common";
import { Matrix3, Matrix4 } from "./Matrix";
import { Vector2, Vector3 } from "./Vector";

const _v2 = new Vector2();
const _v3 = new Vector3();

function denormalize(value: number, array: TypedArray): number {
  switch (array.constructor) {
    case Float32Array:
      return value;
    case Uint32Array:
      return value / 4294967295.0;
    case Uint16Array:
      return value / 65535.0;
    case Uint8Array:
      return value / 255.0;
    case Int32Array:
      return Math.max(value / 2147483647.0, -1.0);
    case Int16Array:
      return Math.max(value / 32767.0, -1.0);
    case Int8Array:
      return Math.max(value / 127.0, -1.0);
    default:
      throw new Error("Invalid component type.");
  }
}

function normalize(value: number, array: TypedArray): number {
  switch (array.constructor) {
    case Float32Array:
      return value;
    case Uint32Array:
      return Math.round(value * 4294967295.0);
    case Uint16Array:
      return Math.round(value * 65535.0);
    case Uint8Array:
      return Math.round(value * 255.0);
    case Int32Array:
      return Math.round(value * 2147483647.0);
    case Int16Array:
      return Math.round(value * 32767.0);
    case Int8Array:
      return Math.round(value * 127.0);
    default:
      throw new Error("Invalid component type.");
  }
}

export class BufferAttribute {
  array: TypedArray;
  itemSize: number;
  count: number;
  normalized: boolean;
  updateRanges: { start: number; count: number }[];
  version: number;

  constructor(array: TypedArray, itemSize: number, normalized = false) {
    if (Array.isArray(array)) {
      throw new TypeError("BufferAttribute: array should be a Typed Array.");
    }
    this.array = array;
    this.itemSize = itemSize;
    this.count = array.length / itemSize;
    this.normalized = normalized;
    this.updateRanges = [];
    this.version = 0;
  }

  _onChangeCallback() {}

  set needsUpdate(value: boolean) {
    if (value === true) this.version++;
  }

  addUpdateRange(start: number, count: number) {
    this.updateRanges.push({ start, count });
  }

  clearUpdateRanges() {
    this.updateRanges.length = 0;
  }

  copy(source: BufferAttribute) {
    // this.array = new source.array.constructor(source.array);
    this.array = cloneTypedArray(source.array);
    this.itemSize = source.itemSize;
    this.count = source.count;
    this.normalized = source.normalized;
    return this;
  }

  copyAt(index1: number, attribute: BufferAttribute, index2: number) {
    index1 *= this.itemSize;
    index2 *= attribute.itemSize;
    for (let i = 0, l = this.itemSize; i < l; i++) {
      this.array[index1 + i] = attribute.array[index2 + i];
    }
    return this;
  }

  copyArray(array: TypedArray) {
    this.array.set(array);
    return this;
  }

  applyMatrix3(m: Matrix3) {
    if (this.itemSize === 2) {
      for (let i = 0, l = this.count; i < l; i++) {
        _v2.fromBufferAttribute(this, i);
        _v2.applyMatrix3(m);
        this.setXY(i, _v2.x, _v2.y);
      }
    } else if (this.itemSize === 3) {
      for (let i = 0, l = this.count; i < l; i++) {
        _v3.fromBufferAttribute(this, i);
        _v3.applyMatrix3(m);
        this.setXYZ(i, _v3.x, _v3.y, _v3.z);
      }
    }
    return this;
  }

  applyMatrix4(m: Matrix4) {
    for (let i = 0, l = this.count; i < l; i++) {
      _v3.fromBufferAttribute(this, i);
      _v3.applyMatrix4(m);
      this.setXYZ(i, _v3.x, _v3.y, _v3.z);
    }
    return this;
  }

  applyNormalMatrix(m: Matrix3) {
    for (let i = 0, l = this.count; i < l; i++) {
      _v3.fromBufferAttribute(this, i);
      _v3.applyNormalMatrix(m);
      this.setXYZ(i, _v3.x, _v3.y, _v3.z);
    }
    return this;
  }

  transformDirection(m: Matrix4) {
    for (let i = 0, l = this.count; i < l; i++) {
      _v3.fromBufferAttribute(this, i);
      _v3.transformDirection(m);
      this.setXYZ(i, _v3.x, _v3.y, _v3.z);
    }
    return this;
  }

  set(value: ArrayLike<number>, offset = 0) {
    // Matching BufferAttribute constructor, do not normalize the array.
    this.array.set(value, offset);
    return this;
  }

  getComponent(index: number, component: number) {
    let value = this.array[index * this.itemSize + component];
    if (this.normalized) value = denormalize(value, this.array);
    return value;
  }

  setComponent(index: number, component: number, value: number) {
    if (this.normalized) value = normalize(value, this.array);
    this.array[index * this.itemSize + component] = value;
    return this;
  }

  getX(index: number) {
    let x = this.array[index * this.itemSize];
    if (this.normalized) x = denormalize(x, this.array);
    return x;
  }

  setX(index: number, x: number) {
    if (this.normalized) x = normalize(x, this.array);
    this.array[index * this.itemSize] = x;
    return this;
  }

  getY(index: number) {
    let y = this.array[index * this.itemSize + 1];
    if (this.normalized) y = denormalize(y, this.array);
    return y;
  }

  setY(index: number, y: number) {
    if (this.normalized) y = normalize(y, this.array);
    this.array[index * this.itemSize + 1] = y;
    return this;
  }

  getZ(index: number) {
    let z = this.array[index * this.itemSize + 2];
    if (this.normalized) z = denormalize(z, this.array);
    return z;
  }

  setZ(index: number, z: number) {
    if (this.normalized) z = normalize(z, this.array);
    this.array[index * this.itemSize + 2] = z;
    return this;
  }

  getW(index: number) {
    let w = this.array[index * this.itemSize + 3];
    if (this.normalized) w = denormalize(w, this.array);
    return w;
  }

  setW(index: number, w: number) {
    if (this.normalized) w = normalize(w, this.array);
    this.array[index * this.itemSize + 3] = w;
    return this;
  }

  setXY(index: number, x: number, y: number) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize(x, this.array);
      y = normalize(y, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    return this;
  }

  setXYZ(index: number, x: number, y: number, z: number) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize(x, this.array);
      y = normalize(y, this.array);
      z = normalize(z, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    this.array[index + 2] = z;
    return this;
  }

  setXYZW(index: number, x: number, y: number, z: number, w: number) {
    index *= this.itemSize;
    if (this.normalized) {
      x = normalize(x, this.array);
      y = normalize(y, this.array);
      z = normalize(z, this.array);
      w = normalize(w, this.array);
    }
    this.array[index + 0] = x;
    this.array[index + 1] = y;
    this.array[index + 2] = z;
    this.array[index + 3] = w;
    return this;
  }

  onChange(callback: () => void) {
    this._onChangeCallback = callback;
    return this;
  }

  clone() {
    // return new this.constructor(this.array, this.itemSize).copy(this);
    return newConstructor(
      this,
      this.array,
      this.itemSize,
      this.normalized
    ).copy(this);
  }

  toJSON() {
    return {
      itemSize: this.itemSize,
      type: this.array.constructor.name,
      array: Array.from(this.array),
      normalized: this.normalized
    };
  }
}

export class Int8BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Int8Array(array), itemSize, normalized);
  }
}

export class Uint8BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Uint8Array(array), itemSize, normalized);
  }
}

export class Uint8ClampedBufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Uint8ClampedArray(array), itemSize, normalized);
  }
}

export class Int16BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Int16Array(array), itemSize, normalized);
  }
}

export class Uint16BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Uint16Array(array), itemSize, normalized);
  }
}

export class Int32BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Int32Array(array), itemSize, normalized);
  }
}

export class Uint32BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Uint32Array(array), itemSize, normalized);
  }
}

// export class Float16BufferAttribute extends BufferAttribute {
//   isFloat16BufferAttribute = true;

//   constructor(array: number[], itemSize: number, normalized = false) {
//     super(new Uint16Array(array), itemSize, normalized);
//   }

//   getX(index: number) {
//     let x = fromHalfFloat(this.array[index * this.itemSize]);
//     if (this.normalized) x = denormalize(x, this.array);
//     return x;
//   }

//   setX(index: number, x: number) {
//     if (this.normalized) x = normalize(x, this.array);
//     this.array[index * this.itemSize] = toHalfFloat(x);
//     return this;
//   }

//   getY(index: number) {
//     let y = fromHalfFloat(this.array[index * this.itemSize + 1]);
//     if (this.normalized) y = denormalize(y, this.array);
//     return y;
//   }

//   setY(index: number, y: number) {
//     if (this.normalized) y = normalize(y, this.array);
//     this.array[index * this.itemSize + 1] = toHalfFloat(y);
//     return this;
//   }

//   getZ(index: number) {
//     let z = fromHalfFloat(this.array[index * this.itemSize + 2]);
//     if (this.normalized) z = denormalize(z, this.array);
//     return z;
//   }

//   setZ(index: number, z: number) {
//     if (this.normalized) z = normalize(z, this.array);
//     this.array[index * this.itemSize + 2] = toHalfFloat(z);
//     return this;
//   }

//   getW(index: number) {
//     let w = fromHalfFloat(this.array[index * this.itemSize + 3]);
//     if (this.normalized) w = denormalize(w, this.array);
//     return w;
//   }

//   setW(index: number, w: number) {
//     if (this.normalized) w = normalize(w, this.array);
//     this.array[index * this.itemSize + 3] = toHalfFloat(w);
//     return this;
//   }

//   setXY(index: number, x: number, y: number) {
//     index *= this.itemSize;
//     if (this.normalized) {
//       x = normalize(x, this.array);
//       y = normalize(y, this.array);
//     }
//     this.array[index + 0] = toHalfFloat(x);
//     this.array[index + 1] = toHalfFloat(y);
//     return this;
//   }

//   setXYZ(index: number, x: number, y: number, z: number) {
//     index *= this.itemSize;
//     if (this.normalized) {
//       x = normalize(x, this.array);
//       y = normalize(y, this.array);
//       z = normalize(z, this.array);
//     }
//     this.array[index + 0] = toHalfFloat(x);
//     this.array[index + 1] = toHalfFloat(y);
//     this.array[index + 2] = toHalfFloat(z);
//     return this;
//   }

//   setXYZW(index: number, x: number, y: number, z: number, w: number) {
//     index *= this.itemSize;
//     if (this.normalized) {
//       x = normalize(x, this.array);
//       y = normalize(y, this.array);
//       z = normalize(z, this.array);
//       w = normalize(w, this.array);
//     }
//     this.array[index + 0] = toHalfFloat(x);
//     this.array[index + 1] = toHalfFloat(y);
//     this.array[index + 2] = toHalfFloat(z);
//     this.array[index + 3] = toHalfFloat(w);
//     return this;
//   }
// }

export class Float32BufferAttribute extends BufferAttribute {
  constructor(array: number[], itemSize: number, normalized = false) {
    super(new Float32Array(array), itemSize, normalized);
  }
}
